// src/components/AISettingsOverlay.js
import React, { useEffect, useMemo, useState } from 'react';

export default function AISettingsOverlay({ adminUsername, onClose }) {

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 720px)').matches
      : false
  );

  const headers = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (adminUsername) h.user = adminUsername;
    return h;
  }, [adminUsername]);

  const [loading,setLoading] = useState(false);
  const [error,setError] = useState('');

  const [aiMode,setAiMode] = useState(false);
  const [savingAiMode,setSavingAiMode] = useState(false);

  const [aiSource,setAiSource] = useState("Open_AI_imagePrompt");
  const [savingSource,setSavingSource] = useState(false);

  const [overallLimit,setOverallLimit] = useState('');
  const [perUserMonthly,setPerUserMonthly] = useState('');
  const [totalPrompted,setTotalPrompted] = useState('0');

  const [savingOverall,setSavingOverall] = useState(false);
  const [savingPerUser,setSavingPerUser] = useState(false);

  const [overallSavedMsg,setOverallSavedMsg] = useState('');
  const [perUserSavedMsg,setPerUserSavedMsg] = useState('');

  useEffect(()=>{

    if(typeof window === 'undefined')
      return;

    const mq =
      window.matchMedia('(max-width: 720px)');

    const onChange =
      (e)=>setIsMobile(!!e.matches);

    mq.addEventListener
      ? mq.addEventListener('change',onChange)
      : mq.addListener(onChange);

    setIsMobile(mq.matches);

    return ()=>{

      mq.removeEventListener
        ? mq.removeEventListener('change',onChange)
        : mq.removeListener(onChange);

    };

  },[]);


  const coerceBool = (v)=>{

    if(typeof v === 'boolean')
      return v;

    if(typeof v === 'number')
      return v !== 0;

    const s =
      String(v ?? '')
      .trim()
      .toLowerCase();

    if(
      s==='true'||
      s==='1'||
      s==='yes'||
      s==='on'
    )
      return true;

    if(
      s==='false'||
      s==='0'||
      s==='no'||
      s==='off'||
      s===''
    )
      return false;

    return false;

  };


  const fetchSettings = async ()=>{

    setLoading(true);
    setError('');

    setOverallSavedMsg('');
    setPerUserSavedMsg('');

    try{

      const res =
        await fetch(
          '/ai-settings/',
          {
            headers:
              adminUsername
                ? {user:adminUsername}
                : {}
          }
        );

      if(!res.ok){

        const t =
          await res.text().catch(()=>'');

        throw new Error(`${res.status} ${t}`);

      }

      const data =
        await res.json();

      const rawAiMode =
        data.ai_mode ??
        data.aiMode ??
        data["AI Mode"];

      setAiMode(
        coerceBool(rawAiMode)
      );

      const rawSource =
        data.AI_Source ??
        data.ai_source ??
        "Open_AI_imagePrompt";

      setAiSource(
        rawSource === "Gemini_imagePrompt"
          ? "Gemini_imagePrompt"
          : "Open_AI_imagePrompt"
      );

      setOverallLimit(
        String(
          data.overall_request_limit ?? ''
        )
      );

      setPerUserMonthly(
        String(
          data.requests_per_user_monthly ?? ''
        )
      );

      setTotalPrompted(
        String(
          data.total_requests_prompted ?? 0
        )
      );

    }
    catch(e){

      console.error(
        '[AISettings] load failed:',
        e
      );

      setError(
        'Failed to load AI settings.'
      );

    }
    finally{

      setLoading(false);

    }

  };


  useEffect(()=>{

    fetchSettings();

  },[]);



  const savePatch = async(body)=>{

    const res =
      await fetch(
        '/ai-settings/',
        {
          method:'POST',
          headers,
          body:JSON.stringify(body)
        }
      );

    if(!res.ok){

      const t =
        await res.text().catch(()=>'');

      throw new Error(
        t || "Save failed"
      );

    }

    await fetchSettings();

  };


  const handleToggleAiMode = async ()=>{

    if(savingAiMode)
      return;

    setError('');

    try{

      setSavingAiMode(true);

      const next =
        !aiMode;

      await savePatch({
        ai_mode:next
      });

      setAiMode(next);

    }
    catch(e){

      console.error(e);

      setError(
        "Failed to update AI Mode."
      );

    }
    finally{

      setSavingAiMode(false);

    }

  };


  const handleToggleSource = async ()=>{

    if(savingSource)
      return;

    setError('');

    try{

      setSavingSource(true);

      const next =
        aiSource === "Open_AI_imagePrompt"
          ? "Gemini_imagePrompt"
          : "Open_AI_imagePrompt";

      await savePatch({

        AI_Source:next

      });

      setAiSource(next);

    }
    catch(e){

      console.error(e);

      setError(
        "Failed to update AI Source."
      );

    }
    finally{

      setSavingSource(false);

    }

  };


  const handleSaveOverall = async ()=>{

    if(savingOverall)
      return;

    setError('');
    setOverallSavedMsg('');

    if(
      !Number.isFinite(
        Number(overallLimit)
      )
    ){

      setError(
        "Overall Request Limit must be a number."
      );

      return;

    }

    try{

      setSavingOverall(true);

      await savePatch({

        overall_request_limit:
          Number(overallLimit)

      });

      setOverallSavedMsg("Saved.");

    }
    catch(e){

      console.error(e);

      setError(
        "Failed to save Overall Request Limit."
      );

    }
    finally{

      setSavingOverall(false);

      setTimeout(
        ()=>setOverallSavedMsg(''),
        2000
      );

    }

  };


  const handleSavePerUser = async ()=>{

    if(savingPerUser)
      return;

    setError('');
    setPerUserSavedMsg('');

    if(
      !Number.isFinite(
        Number(perUserMonthly)
      )
    ){

      setError(
        "Requests Per User must be a number."
      );

      return;

    }

    try{

      setSavingPerUser(true);

      await savePatch({

        requests_per_user_monthly:
          Number(perUserMonthly)

      });

      setPerUserSavedMsg("Saved.");

    }
    catch(e){

      console.error(e);

      setError(
        "Failed to save Requests Per User."
      );

    }
    finally{

      setSavingPerUser(false);

      setTimeout(
        ()=>setPerUserSavedMsg(''),
        2000
      );

    }

  };


  return (

    <div style={styles.backdrop}>

      <div
        style={{
          ...styles.panel,

          width:
            isMobile ? '100%' : '95%',

          maxWidth:
            isMobile ? '100%' : 640,

          borderRadius:
            isMobile
              ? '14px 14px 0 0'
              : 12,

          alignSelf:
            isMobile
              ? 'flex-end'
              : 'center',

          maxHeight:
            isMobile
              ? '92vh'
              : '80vh',
        }}
      >

        <div style={styles.headerRow}>

          <div>

            <div style={styles.title}>
              AI Settings
            </div>

            <div style={styles.subTitle}>

              Admin:
              <strong>
                {adminUsername || "(unknown)"}
              </strong>

            </div>

          </div>

          <button
            onClick={onClose}
            style={btn('#6b7280')}
          >
            Exit
          </button>

        </div>


        {loading && <div>Loading…</div>}

        {error &&
          <div style={{color:'red',marginBottom:10}}>
            {error}
          </div>
        }


        {!loading && (

          <div
            style={{
              display:'flex',
              flexDirection:'column',
              gap:14
            }}
          >

{/* AI MODE */}

<div style={styles.row}>

<div style={styles.label}>
AI Mode
</div>

<div
onClick={()=>{
if(savingAiMode)return;
handleToggleAiMode();
}}

style={{
position:'relative',
width:160,
height:34,
borderRadius:999,
border:'1px solid #d1d5db',
backgroundColor:'#f3f4f6',

display:'flex',
alignItems:'center',
justifyContent:'space-between',

padding:'0 12px',

cursor:
savingAiMode
?'default'
:'pointer',

opacity:
savingAiMode
?0.6
:1,

transition:'opacity .15s',

boxSizing:'border-box',

userSelect:'none'
}}

>

<div
style={{
position:'absolute',

top:2,
bottom:2,

width:'50%',

borderRadius:999,

backgroundColor:
aiMode
?'#10b981'
:'#9ca3af',

left:
aiMode
?'calc(50% - 2px)'
:2,

transition:'left 0.2s ease',

zIndex:0
}}
/>

<span style={{zIndex:1,fontWeight:aiMode?'normal':'bold'}}>
OFF
</span>

<span style={{zIndex:1,fontWeight:aiMode?'bold':'normal'}}>
ON
</span>

</div>

<div style={{marginTop:8,fontSize:12,color:'#6b7280'}}>

Current:
<strong>
{aiMode?'ON':'OFF'}
</strong>

</div>

</div>



{/* AI SOURCE */}

<div style={styles.row}>

<div style={styles.label}>
AI Source
</div>

<div
onClick={()=>{

if(savingSource)
return;

handleToggleSource();

}}

style={{
position:'relative',
width:220,
height:34,

borderRadius:999,

border:'1px solid #d1d5db',

backgroundColor:'#f3f4f6',

display:'flex',
alignItems:'center',
justifyContent:'space-between',

padding:'0 12px',

cursor:
savingSource
?'default'
:'pointer',

opacity:
savingSource
?0.6
:1,

transition:'opacity .15s',

boxSizing:'border-box',

userSelect:'none'
}}

>

<div
style={{

position:'absolute',

top:2,
bottom:2,

width:'50%',

borderRadius:999,

backgroundColor:
aiSource==="Gemini_imagePrompt"
?'#10b981'
:'#9ca3af',

left:
aiSource==="Gemini_imagePrompt"
?'calc(50% - 2px)'
:2,

transition:'left 0.2s ease',

zIndex:0

}}
/>

<span
style={{
zIndex:1,
fontWeight:
aiSource==="Open_AI_imagePrompt"
?'bold'
:'normal'
}}
>
OpenAI
</span>

<span
style={{
zIndex:1,
fontWeight:
aiSource==="Gemini_imagePrompt"
?'bold'
:'normal'
}}
>
Gemini
</span>

</div>

<div style={{marginTop:8,fontSize:12,color:'#6b7280'}}>

Current:
<strong>

{
aiSource==="Gemini_imagePrompt"
?" Gemini"
:" OpenAI"
}

</strong>

</div>

</div>


{/* LIMITS */}

<div style={styles.row}>

<div style={styles.label}>
Overall Request Limit
</div>

<div style={styles.controls}>

<input
type="number"
value={overallLimit}
onChange={(e)=>setOverallLimit(e.target.value)}
style={styles.input}
/>

<button
onClick={handleSaveOverall}
disabled={savingOverall}
style={btn('#111827')}
>
Save
</button>

</div>

{overallSavedMsg &&
<div style={styles.savedMsg}>
{overallSavedMsg}
</div>
}

</div>



<div style={styles.row}>

<div style={styles.label}>
Requests Per User (Monthly)
</div>

<div style={styles.controls}>

<input
type="number"
value={perUserMonthly}
onChange={(e)=>setPerUserMonthly(e.target.value)}
style={styles.input}
/>

<button
onClick={handleSavePerUser}
disabled={savingPerUser}
style={btn('#111827')}
>
Save
</button>

</div>

{perUserSavedMsg &&
<div style={styles.savedMsg}>
{perUserSavedMsg}
</div>
}

</div>


<div style={{...styles.row,background:'#f9fafb'}}>

<div style={styles.label}>
Total Requests Prompted
</div>

<div style={{
fontSize:22,
fontWeight:900,
textAlign:'center',
padding:'10px 0'
}}>
{totalPrompted}
</div>

</div>

</div>

)}

</div>

</div>

);

}

const styles={

backdrop:{
position:'fixed',
inset:0,
backgroundColor:'rgba(0,0,0,0.45)',

display:'flex',

justifyContent:'center',

alignItems:'center',

zIndex:10050,

padding:12
},

panel:{
backgroundColor:'white',
padding:16,
boxShadow:'0 0 20px rgba(0,0,0,0.4)',
overflow:'auto'
},

headerRow:{
display:'flex',
justifyContent:'space-between',
marginBottom:14,
gap:10
},

title:{
fontSize:'1.1rem',
fontWeight:900
},

subTitle:{
fontSize:12,
color:'#6b7280',
marginTop:4
},

row:{
border:'1px solid #e5e7eb',
borderRadius:10,
padding:12
},

label:{
fontWeight:800,
marginBottom:8
},

controls:{
display:'flex',
gap:10,
alignItems:'center',
flexWrap:'wrap'
},

input:{
padding:'8px 10px',
border:'1px solid #d1d5db',
borderRadius:8,
minWidth:160
},

savedMsg:{
marginTop:8,
fontSize:12,
color:'#10b981',
fontWeight:800
}

};


function btn(bg){

return{

padding:'8px 14px',

backgroundColor:bg,

color:'white',

fontWeight:'bold',

border:'none',

borderRadius:'6px',

cursor:'pointer',

whiteSpace:'nowrap'

};

}